import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import PropTypes from 'prop-types';

const TextWithInlineLink = ({ text = '', linkText, href }) => {
	const index = linkText ? text.indexOf(linkText) : -1;

	if (index === -1) {
		return (
			<>
				{text}
				<Link href={href}>{linkText || href}</Link>
			</>
		);
	}

	return (
		<>
			{text.slice(0, index)}
			<Link href={href}>{linkText}</Link>
			{text.slice(index + linkText.length)}
		</>
	);
};

TextWithInlineLink.propTypes = {
	text: PropTypes.string,
	linkText: PropTypes.string,
	href: PropTypes.string.isRequired,
};

export const ErrorMessage = ({ content }) => {
	return (
		<Box sx={{ py: 6 }}>
			<Typography variant='h1' gutterBottom>
				{content.heading}
			</Typography>
			<Typography variant='body1' gutterBottom>
				{content.body}
			</Typography>
			<Typography variant='body1' sx={{ fontStyle: 'italic' }}>
				{content.footnote}
			</Typography>
		</Box>
	);
};

ErrorMessage.propTypes = {
	content: PropTypes.shape({
		heading: PropTypes.string,
		body: PropTypes.string,
		footnote: PropTypes.string,
	}).isRequired,
};

export const InvalidPlanMessage = () => (
	<Typography>
		Please visit your health plan&apos;s website to search for a
		participating provider or call ASH customer service at (800) 678-9133.
		If you feel that this page is appearing in error, please ensure that
		your browser has cookies enabled for this site before retrying.
	</Typography>
);

export const PlanEligibilityNotice = ({ content }) => {
	return (
		<Box mb={2}>
			{content.heading && (
				<Typography
					component='span'
					variant='subtitle1'
					fontWeight='bold'
					display='block'
				>
					{content.heading}
				</Typography>
			)}
			<Typography variant='body1'>{content.body}</Typography>
		</Box>
	);
};

PlanEligibilityNotice.propTypes = {
	content: PropTypes.shape({
		heading: PropTypes.string,
		body: PropTypes.string,
	}).isRequired,
};

export const PlanNavigationNotice = ({ content }) => {
	return (
		<Box mb={2}>
			{content.items.map((item, i) => (
				<Typography key={i} variant='body1' gutterBottom>
					{item.label && (
						<Typography component='span' fontWeight='bold'>
							{item.label}:{' '}
						</Typography>
					)}
					{item.href ? (
						<TextWithInlineLink
							text={item.text}
							linkText={item.linkText}
							href={item.href}
						/>
					) : (
						item.text
					)}
				</Typography>
			))}
		</Box>
	);
};

PlanNavigationNotice.propTypes = {
	content: PropTypes.shape({
		items: PropTypes.arrayOf(
			PropTypes.shape({
				label: PropTypes.string,
				href: PropTypes.string,
				text: PropTypes.string,
				linkText: PropTypes.string,
			})
		).isRequired,
	}).isRequired,
};

export const Disclaimer = ({ content }) => {
	const isSharp = content.hasEmbeddedLanguageAccess;
	return (
		<Box mb={2}>
			<Typography variant='body2' gutterBottom>
				<Typography component='span' fontWeight='bold'>
					Disclaimer:{' '}
				</Typography>
				{content.text}
			</Typography>
			{isSharp && (
				<>
					<Typography variant='body2' gutterBottom>
						Enrollees are entitled to full and equal access to
						covered services, including enrollees with disabilities
						as required under the federal Americans with
						Disabilities Act of 1990 and Section 504 of the
						Rehabilitation Act of 1973.
					</Typography>
					<Typography variant='body2' gutterBottom>
						ASH operates an &ldquo;open access&rdquo; model in which
						any qualified provider/practitioner can submit an
						application and signed agreement to be considered for
						network participation. For more information on
						ASH&rsquo;s open network, visit ASH&rsquo;s recruitment
						pages on ASHLink.com.
					</Typography>
				</>
			)}
		</Box>
	);
};

Disclaimer.propTypes = {
	content: PropTypes.shape({
		text: PropTypes.string,
		hasEmbeddedLanguageAccess: PropTypes.bool,
	}).isRequired,
};

export const DirectoryAccuracyBlock = ({ content, children }) => {
	return (
		<Box mb={2}>
			<Typography variant='body2' gutterBottom>
				{content.accuracy.split(content.accuracyEmail)[0]}
				<Link href={`mailto:${content.accuracyEmail}`}>
					{content.accuracyEmail}
				</Link>
				{', '}complete an{' '}
				<Link href={content.accuracyFormHref}>online form</Link>, or
				call provider services at 1-800-972-4226 option 4.
			</Typography>
			<Typography variant='body2' gutterBottom>
				{content.ada}
			</Typography>
			<Typography variant='body2' gutterBottom>
				{content.openAccess}
			</Typography>
			{/* Language access is nested inside this block, matching the ASPX DOM structure */}
			{children}
		</Box>
	);
};

DirectoryAccuracyBlock.propTypes = {
	content: PropTypes.shape({
		accuracy: PropTypes.string,
		accuracyEmail: PropTypes.string,
		accuracyFormHref: PropTypes.string,
		ada: PropTypes.string,
		openAccess: PropTypes.string,
	}).isRequired,
	children: PropTypes.node,
};

export const PriorityHealthMemberActions = ({ content }) => {
	return (
		<Box mb={2}>
			{content.items.map((item, i) => (
				<Typography key={i} variant='body2' gutterBottom>
					{item.href ? (
						<TextWithInlineLink
							text={item.text}
							linkText={item.linkText}
							href={item.href}
						/>
					) : (
						item.text
					)}
				</Typography>
			))}
		</Box>
	);
};

PriorityHealthMemberActions.propTypes = {
	content: PropTypes.shape({
		items: PropTypes.arrayOf(
			PropTypes.shape({
				href: PropTypes.string,
				text: PropTypes.string,
				linkText: PropTypes.string,
			})
		).isRequired,
	}).isRequired,
};

export const LanguageAccess = ({ type, content }) => {
	if (type === 'standard') {
		return (
			<Box mb={2}>
				{content.languages.map(l => (
					<Typography
						key={l.lang}
						variant='body2'
						gutterBottom
						lang={l.lang}
					>
						<Typography component='span' fontWeight='bold'>
							{l.label}:{' '}
						</Typography>
						{l.text}
					</Typography>
				))}
			</Box>
		);
	}

	if (type === 'fallon') {
		return (
			<Box mb={2}>
				<Typography variant='body2' gutterBottom>
					<Typography component='span' fontWeight='bold'>
						Important:{' '}
					</Typography>
					If you have language needs other than English, we can have
					somebody help you. {content.notes[0]}
				</Typography>
				{content.notes.slice(1).map((note, i) => (
					<Typography key={i} variant='body2' gutterBottom>
						{note}
					</Typography>
				))}
			</Box>
		);
	}

	if (type === 'calviva') {
		return (
			<Box mb={2}>
				{content.links.map(link => (
					<Typography key={link.href} variant='body2' gutterBottom>
						<Link href={link.href} target='_blank' rel='noreferrer'>
							{link.text}
						</Link>
					</Typography>
				))}
			</Box>
		);
	}

	if (type === 'blueshield') {
		return (
			<Box mb={2}>
				<Typography variant='h6' gutterBottom>
					{content.heading}
				</Typography>
				<Typography
					variant='body2'
					sx={{ fontStyle: 'italic' }}
					gutterBottom
				>
					{content.subtext}
				</Typography>
				<Box
					component='img'
					src={content.imageUrl}
					alt=''
					sx={{ maxWidth: '100%', display: 'block', mb: 1 }}
				/>
				<Typography variant='body2'>
					<Link
						href={content.nondiscriminationLink.href}
						target='_blank'
						rel='noreferrer'
					>
						{content.nondiscriminationLink.text}
					</Link>
				</Typography>
			</Box>
		);
	}

	if (type === 'chw') {
		return (
			<Box mb={2}>
				{content.links.map(link => (
					<Typography key={link.href} variant='body2' gutterBottom>
						<Link href={link.href} target='_blank' rel='noreferrer'>
							{link.text}
						</Link>
					</Typography>
				))}
			</Box>
		);
	}

	return null;
};

LanguageAccess.propTypes = {
	type: PropTypes.string.isRequired,
	content: PropTypes.object.isRequired,
};

export const CaTimelyAccess = ({ content }) => {
	return (
		<Box mb={2}>
			<Divider sx={{ mb: 2 }} />
			<Typography
				variant='subtitle2'
				fontWeight='bold'
				sx={{ textDecoration: 'underline' }}
				gutterBottom
			>
				{content.heading}
			</Typography>
			<Typography variant='body2' gutterBottom>
				{content.intro}
			</Typography>
			<List dense disablePadding>
				{content.standards.map((s, i) => (
					<ListItem
						key={i}
						sx={{
							display: 'list-item',
							listStyleType: 'disc',
							ml: 3,
							pl: 0,
						}}
					>
						<Typography variant='body2'>{s}</Typography>
					</ListItem>
				))}
			</List>
		</Box>
	);
};

CaTimelyAccess.propTypes = {
	content: PropTypes.shape({
		heading: PropTypes.string,
		intro: PropTypes.string,
		standards: PropTypes.arrayOf(PropTypes.string),
	}).isRequired,
};

export const FooterLanguageLinks = ({ links }) => {
	return (
		<Box>
			<Typography
				variant='h6'
				fontWeight='bold'
				color='white'
				gutterBottom
			>
				Language Access Services
			</Typography>
			<List
				dense
				disablePadding
				sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
			>
				{links.map(link => (
					<ListItem
						key={link.href}
						lang={link.lang}
						dir={link.dir}
						sx={{ width: 'auto', p: 0 }}
					>
						<Link
							href={link.href}
							target='_blank'
							rel='noreferrer'
							variant='body1'
							color='inherit'
							underline='hover'
						>
							{link.label}
						</Link>
					</ListItem>
				))}
			</List>
		</Box>
	);
};

FooterLanguageLinks.propTypes = {
	links: PropTypes.arrayOf(
		PropTypes.shape({
			href: PropTypes.string.isRequired,
			lang: PropTypes.string,
			dir: PropTypes.string,
			label: PropTypes.string,
		})
	).isRequired,
};
